import { AgentRun } from '@lucidtech/las-sdk-browser';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { useClient } from '@/hooks';
import {
  getActionRunOptions,
  getAgentRunOptions,
  getValidationTaskOptions,
  toActionRunId,
  toAgentRunId,
  toValidationTaskId,
  useCreateActionRun,
  useUpdateValidationTask,
} from '@/hooks/api';
import { fromActionRunId, getResourceIds, isValidationTaskId } from '@/utils';

export type RerunError = {
  error: string;
  runId?: string;
};

export type UseRerunAgentRunOpts = { agentId?: string | null };

export const useRerunAgentRun = ({ agentId }: UseRerunAgentRunOpts) => {
  const queryClient = useQueryClient();
  const { client } = useClient();
  const { mutateAsync: updateTask } = useUpdateValidationTask();
  const { mutateAsync: createActionRun } = useCreateActionRun();

  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errors, setErrors] = useState<RerunError[]>([]);
  const [progress, setProgress] = useState<number>(0);

  const rerun = useCallback(
    async (runIds: string[], selectActionFn: (agentRun: AgentRun) => string | undefined) => {
      if (!runIds.length) return;

      setErrors([]);
      setIsError(false);
      setIsPending(true);
      setProgress(0);

      const addError = (error: string, runId: string) => {
        setErrors((errs) => [...errs, { error, runId }]);
      };

      for (const [i, runId] of runIds.entries()) {
        let agentRun;
        try {
          agentRun = await queryClient.ensureQueryData(
            getAgentRunOptions(client, queryClient, toAgentRunId(agentId, runId))
          );
        } catch (error) {
          if (error instanceof Error) {
            addError(error.message, runId);
          } else {
            addError('Failed to fetch agent run.', runId);
          }
          continue;
        }

        const lastActionRunId = selectActionFn(agentRun);
        if (!lastActionRunId) {
          continue;
        }

        const { actionId } = fromActionRunId(lastActionRunId);

        if (!lastActionRunId) {
          addError('No create prediction action run found.', runId);
          continue;
        }

        let actionRun;
        try {
          actionRun = await queryClient.ensureQueryData(
            getActionRunOptions(client, queryClient, toActionRunId(lastActionRunId))
          );
        } catch (error) {
          if (error instanceof Error) {
            addError(error.message, runId);
          } else {
            addError('Failed to fetch action run.', runId);
          }
          continue;
        }

        for (const taskId of new Set(getResourceIds(agentRun).filter(isValidationTaskId))) {
          const id = toValidationTaskId(undefined, taskId);
          const task = await queryClient.ensureQueryData(getValidationTaskOptions(client, queryClient, id));
          if (task.status === 'ready') {
            updateTask({
              id,
              updates: { status: 'cancelled', output: { statusReason: 'rerun' } },
            }).catch(console.error);
          }
        }

        try {
          await createActionRun({
            actionId,
            agentRunId: `${agentId}/${runId}`,
            input: actionRun.input,
          });
        } catch (error) {
          if (error instanceof Error) {
            addError(error.message, runId);
          } else {
            addError('Failed to create action run.', runId);
          }
          continue;
        }

        setProgress((i + 1) / runIds.length);
      }

      setIsPending(false);
    },
    [agentId, client, createActionRun, queryClient, updateTask]
  );

  return { rerun, isPending, isError, errors, progress };
};
